import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useRef } from "react"

import { AgentIterationStyles } from "@/components/AgentIteration/AgentIterationStyles.ts"
import { CodeSnippet } from "@/components/CodeSnippet/CodeSnippet.tsx"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { Vertical } from "@/components/Flex.ts"
import { ExecResultFullReloadModel } from "@/components/ReasoningStep/ExecResultTerminalModel.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useOrgContext } from "@/hooks/useRootContext.ts"

import type { TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { ReactNode } from "react"

export const CodeSnippetDialog = observer((props: {
  execResultId: TID | null,
  taskId: string | null,
  children: ReactNode,
  title?: string
}) => {
  const context = useOrgContext()

  const model = useModel(() => {
    if (!props.execResultId || !props.taskId) {
      return null
    }

    return new ExecResultFullReloadModel({
      execResultId: props.execResultId,
      queries: context.queries,
      taskId: props.taskId
    })
  }, [props.execResultId, props.taskId])

  const ref = useRef<HTMLDivElement>(null)

  if (model == null) {
    return (
      <>
        {props.children}
      </>
    )
  }

  const text = model.stdout.get()

  return (
    <Dialog
      title={props.title ?? "Command Output"}
      trigger={
        <ClickableStep>
          {props.children}
        </ClickableStep>
      }
    >
      <Container ref={ref}>
        <CodeSnippet text={text} language="text" />
      </Container>
    </Dialog>
  )
})

const Container = styled(Vertical)`
  overflow: auto;

  max-height: calc(90vh - 78px);

  flex-shrink: 1;
`

const ClickableStep = styled.div`
  cursor: pointer;

  &:hover {
    .${AgentIterationStyles.secondLevelMarkdown} {
      color: var(--main-color);
    }
  }
`
